import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { Table } from 'app-components';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { usePermissions } from '../../../../hooks/usePermissions';
import { DATE_FORMATS, formatDateString } from '../../../../utils/dates/dateMethods';
import { IEnrollmentSnapshotResponse } from '../common/types';
import useEnrollmentModal from '../hooks/useEnrollmentModal';
import useEnrollmentModalAPI from '../hooks/useEnrollmentModalAPI';

const StyledSection = styled.section`
    margin: 20px 0;
    table {
        margin: 0;
        text-align: center !important;
    }
    > div {
        height: 140px;
        overflow: auto;
    }
    thead tr th {
        position: sticky;
        text-align: center;
        top: 0;
    }
`;

const StyledHeading = styled.h4`
    margin: 1rem 0;
`;

const StyledLink = styled.a`
    color:  ${({ theme }) => theme.primaryMain} !important};
    cursor: pointer;
  `;

interface IEventHistoryTableProps {
    enrollmentHistoryList: any[];
    heading: string;
    enrollmentId: number;
}

const EventHistoryTable: React.FC<IEventHistoryTableProps> = ({
    enrollmentHistoryList = [],
    heading,
    enrollmentId,
}) => {
    const intl = useIntl();
    const loggedInUserRole = useSelector((state: any) => state.common.environment.user.role);
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};

    const { handleCancelTerminate, premiumHistory } = useEnrollmentModal();
    const { getEnrollmentSnapshot } = useEnrollmentModalAPI();

    const handleCancelTerminateClick = useCallback(async enrollmentHistory => {
        const payload = {
            enrollmentId: enrollmentId,
            subscriberEventId: enrollmentHistory.subscriberEventId,
        };
        const enrollmentShanpshot: IEnrollmentSnapshotResponse = await getEnrollmentSnapshot(payload);
        handleCancelTerminate(enrollmentShanpshot, premiumHistory);
    }, []);

    const { isAgencyUser } = usePermissions();

    const agencyUser = useMemo(() => isAgencyUser(loggedInUserRole), [loggedInUserRole, isAgencyUser]);

    const getEventHistoryList = useMemo(() => {
        return enrollmentHistoryList.reverse();
    }, [enrollmentHistoryList]);

    const columns = useMemo(
        () => [
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.time' }),
                key: 'eventCreationDate',
                render: (data: any) => <>{formatDateString(data.eventCreationDate, DATE_FORMATS.MM_DD_YYYY_H_MM_SS)}</>,
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.event' }),
                key: 'enrollmentEvent',
                render: (data: any) => {
                    return userPermissions?.memberEnrlmntPrmHist && !agencyUser ? (
                        <StyledLink onClick={() => handleCancelTerminateClick(data)}>{data.enrollmentEvent}</StyledLink>
                    ) : (
                        <span>{data.enrollmentEvent}</span>
                    );
                },
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.maintenanceReason' }),
                key: 'enrollmentEventReason',
            },
            {
                titleId: intl.formatMessage({ id: 'member.enrollment_list.premiumhistory.createdBy' }),
                key: 'eventCreatedBy',
                render: (data: any) => (
                    <>
                        <ConditionalRendering isTrue={agencyUser}>{data.userRole}</ConditionalRendering>
                        <ConditionalRendering isTrue={!agencyUser}>
                            {data.eventCreatedBy} ({data.userRole})
                        </ConditionalRendering>
                    </>
                ),
            },
        ],
        [intl, userPermissions, isAgencyUser],
    );

    return (
        <StyledSection>
            <StyledHeading>{intl.formatMessage({ id: heading })}</StyledHeading>
            <Table columns={columns} data={getEventHistoryList} classNamesStr="enrollment_history" />
        </StyledSection>
    );
};

export default EventHistoryTable;
